/**
 * 持久化记忆系统组件
 *
 * 展示记忆系统功能 + novelclaw支持多平台
 * SVG 连线脉冲动画 + 平台图标间数据流动效果
 */

import React, { useState, useEffect } from 'react';
import { homePageContent } from './homePageContent';

// ============ 记忆同步动画演示组件 ============
const MemorySyncDemo: React.FC = () => {
  const [activePath, setActivePath] = useState(0);
  const [syncedPlatforms, setSyncedPlatforms] = useState<number[]>([]);
  const [showSyncCard, setShowSyncCard] = useState(false);

  const platforms = [
    { name: '微信', icon: '💬', color: '#22c55e', x: 80, y: 80 },
    { name: '钉钉', icon: '📱', color: '#3b82f6', x: 320, y: 80 },
    { name: '飞书', icon: '🚀', color: '#6366f1', x: 200, y: 200 },
  ];

  const centerNode = { name: '记忆核心', color: '#0ea5e9', x: 200, y: 80 };

  // 连线定义: [起点平台索引, 终点平台索引]
  const connections = [
    { from: -1, to: 0 }, // 核心 -> 微信
    { from: -1, to: 1 }, // 核心 -> 钉钉
    { from: -1, to: 2 }, // 核心 -> 飞书
    { from: 0, to: 2 },  // 微信 -> 飞书
    { from: 1, to: 2 },  // 钉钉 -> 飞书
  ];

  useEffect(() => {
    const runAnimation = () => {
      setActivePath(0);
      setSyncedPlatforms([]);
      setShowSyncCard(false);

      // 依次激活连线
      connections.forEach((_, i) => {
        setTimeout(() => {
          setActivePath(i + 1);
        }, i * 600);
      });

      // 平台依次标记为已同步
      platforms.forEach((_, i) => {
        setTimeout(() => {
          setSyncedPlatforms(prev => [...prev, i]);
        }, 1500 + i * 400);
      });

      // 显示同步完成卡片
      setTimeout(() => {
        setShowSyncCard(true);
      }, 3000);

      // 重置循环
      setTimeout(() => {
        runAnimation();
      }, 5500);
    };

    const initialDelay = setTimeout(() => {
      runAnimation();
    }, 500);

    return () => clearTimeout(initialDelay);
  }, []);

  const getNodePos = (index: number) => {
    if (index === -1) return centerNode;
    return platforms[index];
  };

  return (
    <div className="w-full h-full relative">
      <svg className="w-full h-full" viewBox="0 0 400 280">
        {/* 定义渐变和滤镜 */}
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#0ea5e9" stopOpacity="1" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.3" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 连线 */}
        {connections.map((conn, i) => {
          const from = getNodePos(conn.from);
          const to = getNodePos(conn.to);
          const isActive = i < activePath;
          const isCurrent = i === activePath - 1;

          return (
            <g key={i}>
              {/* 背景线 */}
              <line
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke="#e5e7eb"
                strokeWidth="2"
                strokeDasharray="6 4"
              />
              {/* 激活线 */}
              {isActive && (
                <line
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke="url(#lineGradient)"
                  strokeWidth="2.5"
                  filter="url(#glow)"
                  className="transition-all duration-500"
                >
                  {isCurrent && (
                    <animate
                      attributeName="stroke-dasharray"
                      values="0 200;200 0"
                      dur="0.6s"
                      repeatCount="1"
                      fill="freeze"
                    />
                  )}
                </line>
              )}
              {/* 流动点 */}
              {isCurrent && (
                <circle r="4" fill="#0ea5e9" filter="url(#glow)">
                  <animateMotion
                    dur="0.6s"
                    repeatCount="1"
                    path={`M${from.x},${from.y} L${to.x},${to.y}`}
                    fill="freeze"
                  />
                </circle>
              )}
            </g>
          );
        })}

        {/* 中心节点 - 记忆核心 */}
        <g>
          <circle
            cx={centerNode.x}
            cy={centerNode.y}
            r="28"
            fill="#f0f9ff"
            stroke="#0ea5e9"
            strokeWidth="2.5"
            className="transition-all duration-150"
          >
            <animate
              attributeName="r"
              values="28;30;28"
              dur="2s"
              repeatCount="indefinite"
            />
          </circle>
          <text
            x={centerNode.x}
            y={centerNode.y - 5}
            textAnchor="middle"
            className="text-[10px] font-bold fill-sky-600"
          >
            记忆
          </text>
          <text
            x={centerNode.x}
            y={centerNode.y + 8}
            textAnchor="middle"
            className="text-[10px] font-bold fill-sky-600"
          >
            核心
          </text>
          {/* 脉冲环 */}
          <circle
            cx={centerNode.x}
            cy={centerNode.y}
            r="28"
            fill="none"
            stroke="#0ea5e9"
            strokeWidth="1"
            opacity="0.5"
          >
            <animate
              attributeName="r"
              values="28;40"
              dur="1.5s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.5;0"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </circle>
        </g>

        {/* 平台节点 */}
        {platforms.map((platform, i) => {
          const isSynced = syncedPlatforms.includes(i);
          return (
            <g key={i}>
              <circle
                cx={platform.x}
                cy={platform.y}
                r="24"
                fill={isSynced ? platform.color + '15' : '#f9fafb'}
                stroke={isSynced ? platform.color : '#d1d5db'}
                strokeWidth="2"
                className="transition-all duration-500"
              />
              <text
                x={platform.x}
                y={platform.y + 4}
                textAnchor="middle"
                fontSize="16"
              >
                {platform.icon}
              </text>
              <text
                x={platform.x}
                y={platform.y + 38}
                textAnchor="middle"
                className={`text-[10px] font-medium transition-colors duration-500 ${
                  isSynced ? 'fill-gray-700' : 'fill-gray-400'
                }`}
              >
                {platform.name}
              </text>
              {/* 同步完成标记 */}
              {isSynced && (
                <g>
                  <circle
                    cx={platform.x + 18}
                    cy={platform.y - 18}
                    r="8"
                    fill={platform.color}
                    className="animate-ping"
                    opacity="0.3"
                  />
                  <circle
                    cx={platform.x + 18}
                    cy={platform.y - 18}
                    r="8"
                    fill={platform.color}
                  />
                  <text
                    x={platform.x + 18}
                    y={platform.y - 14}
                    textAnchor="middle"
                    fontSize="10"
                    fill="white"
                  >
                    ✓
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {/* 同步完成浮动卡片 */}
      <div
        className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 transition-all duration-700 ${
          showSyncCard ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="bg-white rounded-xl shadow-lg px-4 py-2.5 border border-gray-100 flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-sm font-medium text-gray-700">记忆已同步</span>
          <span className="text-xs text-gray-400">跨平台无缝衔接</span>
        </div>
      </div>
    </div>
  );
};

export const MemorySystem: React.FC = () => {
  const { memorySystem } = homePageContent;

  const platforms = [
    { name: '微信', icon: '💬', color: 'bg-green-100 text-green-700' },
    { name: '钉钉', icon: '📱', color: 'bg-blue-100 text-blue-700' },
    { name: '飞书', icon: '🚀', color: 'bg-indigo-100 text-indigo-700' },
  ];

  return (
    <section className="py-24 px-4 md:px-8 relative">

      <div className="max-w-6xl mx-auto">
        {/* 标题区域 */}
        <div className="text-center mb-16">
        <span className="inline-block px-4 py-1.5 rounded-full bg-paper-200 text-paper-800 text-sm font-medium mb-4">
          {memorySystem.badge}
        </span>
        <h2 className="text-4xl md:text-5xl font-bold text-ink-900 mb-4">
          {memorySystem.title}
        </h2>
        <p className="text-xl text-ink-600 max-w-2xl mx-auto">
            {memorySystem.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* 左侧：记忆系统介绍 */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-paper-500 to-paper-400 flex items-center justify-center shadow-sm">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-ink-900">{memorySystem.memoryTitle}</h3>
                  <p className="text-sm text-ink-500">{memorySystem.memorySubtitle}</p>
                </div>
              </div>

              <p className="text-ink-600 mb-6 leading-relaxed">
                {memorySystem.memoryDescription}
              </p>

              <ul className="space-y-3">
                {memorySystem.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-ink-700">
                    <svg className="w-5 h-5 text-paper-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* NovelClaw 介绍 */}
            <div className="bg-gradient-to-br from-ink-800 to-ink-700 rounded-2xl shadow-sm p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <span className="text-2xl">🦞</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{memorySystem.novelclawTitle}</h3>
                  <p className="text-white/80 text-sm">{memorySystem.novelclawSubtitle}</p>
                </div>
              </div>

              <p className="text-white/90 mb-6 leading-relaxed">
                {memorySystem.novelclawDescription}
              </p>

              {/* 支持的平台 */}
              <div className="flex flex-wrap gap-3">
                {platforms.map((platform, index) => (
                  <span
                    key={index}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${platform.color} text-sm font-medium`}
                  >
                    <span>{platform.icon}</span>
                    {platform.name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* 右侧：动画演示 - 替代原来的静态图片 */}
            <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-sm bg-gradient-to-br from-paper-100 to-paper-50 p-6 h-[320px]">
              <MemorySyncDemo />
            </div>

            {/* 浮动卡片 */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-md p-4 border border-paper-200 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">记忆已同步</p>
                  <p className="text-xs text-gray-500">跨平台无缝衔接</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
